import React, { useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  TextField,
  Typography,
  Grid,
  Collapse,
  IconButton,
} from "@mui/material";
import { Controller, useForm, useFieldArray } from "react-hook-form";
import { useDialog } from "../../DialogContext";
import {
  generateUniqueMatterName,
  pageScrollStyle,
} from "../../../styles/styles";
import {
  handleMatterNewTab,
  ListboxComponent,
  NoDataOptionField,
} from "../../../static/staticdata";
import { useDispatch, useSelector } from "react-redux";
import { getCases } from "../../../redux/Apps/Lawyerapp/CaseSlice/CaseSlice";
import {
  getAllDocFolder,
  getFolderById,
} from "../../../redux/Apps/Lawyerapp/DocumentSlice/DocumentSlice";
import { backend_api } from "../../../static/server";
import { useNavigate } from "react-router-dom";
import { LoadingButton } from "@mui/lab";
import { toast } from "react-toastify";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { CloseRounded } from "@mui/icons-material";
import FolderIcon from "@mui/icons-material/Folder";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DocDropZoneField from "./DocDropZoneField";
import axios from "axios";

const file_type = [
  "image/*", // All image types
  "video/*", // All video types
  "audio/*", // All audio types
  "text/*", // All text files
  ".pdf", // PDF files
  ".doc", // Word documents
  ".docx", // Word documents (new format)
  ".xls", // Excel spreadsheets
  ".xlsx", // Excel spreadsheets (new format)
  ".ppt", // PowerPoint presentations
  ".pptx", // PowerPoint presentations (new format)
  ".txt", // Text files
  ".csv", // CSV files
  ".zip", // Zip archives
  ".rar",
];

const FolderTree = ({
  folders,
  selectedFolder,
  onFolderSelect,
  onFolderExpand,
  expandedFolders,
  loadingFolders,
  level = 0,
}) => {
  const toggleFolder = async (folder) => {
    const folderId = folder.id;

    // If folder is not expanded and has children, expand and load data
    if (!expandedFolders.has(folderId)) {
      // Call the parent component to handle folder expansion and data loading
      await onFolderExpand(folder);
    } else {
      // Just toggle the expansion state
      onFolderExpand(folder, true); // true indicates just toggle, don't load
    }
  };

  return (
    <Box>
      {folders?.map((folder) => (
        <Box key={folder.id}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              pl: level * 2,
              py: 0.5,
              cursor: "pointer",
              borderRadius: "4px",
              bgcolor: selectedFolder === folder.id ? "#e3f2fd" : "transparent",
              "&:hover": {
                bgcolor: selectedFolder === folder.id ? "#e3f2fd" : "#f5f5f5",
              },
            }}
            onClick={() => onFolderSelect(folder?.id, folder?.folder_name)}
          >
            {/* Show expand/collapse button if folder has children or might have children */}
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                toggleFolder(folder);
              }}
              sx={{ p: 0.5, mr: 0.5 }}
              disabled={loadingFolders.has(folder.id)}
            >
              {loadingFolders.has(folder.id) ? (
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    border: "2px solid #ddd",
                    borderTop: "2px solid #1976d2",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                    "@keyframes spin": {
                      "0%": { transform: "rotate(0deg)" },
                      "100%": { transform: "rotate(360deg)" },
                    },
                  }}
                />
              ) : expandedFolders?.has(folder.id) ? (
                <ExpandMoreIcon fontSize="small" />
              ) : (
                <ChevronRightIcon fontSize="small" />
              )}
            </IconButton>

            {expandedFolders.has(folder.id) ? (
              <FolderOpenIcon
                sx={{
                  fontSize: "1.2rem",
                  color: "#ffa726",
                  mr: 1,
                }}
              />
            ) : (
              <FolderIcon
                sx={{
                  fontSize: "1.2rem",
                  color: "#ffa726",
                  mr: 1,
                }}
              />
            )}

            <Typography
              variant="body2"
              sx={{
                fontSize: "0.9rem",
                fontWeight: selectedFolder === folder.id ? 600 : 400,
              }}
            >
              {folder?.folder_name}
            </Typography>
          </Box>

          {/* Show children if folder is expanded and has loaded children */}
          {folder.child_folder && folder.child_folder.length > 0 && (
            <Collapse in={expandedFolders.has(folder.id)}>
              <FolderTree
                folders={folder?.child_folder}
                selectedFolder={selectedFolder}
                onFolderSelect={onFolderSelect}
                onFolderExpand={onFolderExpand}
                expandedFolders={expandedFolders}
                loadingFolders={loadingFolders}
                level={level + 1}
              />
            </Collapse>
          )}
        </Box>
      ))}
    </Box>
  );
};

const UploadFileForm = ({ fileData }) => {
  const { closeDialog } = useDialog();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const hasAppended = useRef(false);
  const { token } = useSelector((state) => state.user);
  const { allDocFolder, currentFolderData } = useSelector(
    (state) => state.document
  );

  // Local state for managing folder tree
  const [folderTree, setFolderTree] = useState([]);
  const [selectedFolderData, setSelectedFolderData] = useState({});
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [loadingFolders, setLoadingFolders] = useState(new Set());
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    dispatch(getCases({ url: `${backend_api}case/register/`, token: token }));
    dispatch(
      getAllDocFolder({
        url: `${backend_api}documents/document/folder/?parent__isnull=true`,
        token: token,
      })
    );
  }, [dispatch, token]);

  useEffect(() => {
    if (allDocFolder) {
      setFolderTree(allDocFolder);
    }
  }, [allDocFolder]);

  useEffect(() => {
    if (currentFolderData && currentFolderData.child_folder) {
      updateFolderInTree(currentFolderData.id, currentFolderData.child_folder);
    }
  }, [currentFolderData]);

  const updateFolderInTree = (folderId, childFolders) => {
    const updateFolder = (folders) => {
      return folders.map((folder) => {
        if (folder.id === folderId) {
          return {
            ...folder,
            child_folder: childFolders,
          };
        } else if (folder.child_folder && folder.child_folder.length > 0) {
          return {
            ...folder,
            child_folder: updateFolder(folder.child_folder),
          };
        }
        return folder;
      });
    };

    setFolderTree((prevTree) => updateFolder(prevTree));
  };

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      files: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "files",
  });

  useEffect(() => {
    if (fileData && !hasAppended.current) {
      const filesArray = Array.isArray(fileData)
        ? fileData
        : Array.from(fileData);
      console.log("Appending files:", filesArray);
      filesArray.forEach((file) => {
        append({
          file: file,
          file_name: file.name,
        });
      });
      hasAppended.current = true;
    }
  }, [fileData, append]);

  const handleMultiFileChange = (files) => {
    if (files) {
      Array.from(files).forEach((file) => {
        append({
          file: file,
          file_name: file.name,
        });
      });
    }
  };

  const handleFolderSelect = (folderId, folderName) => {
    setSelectedFolderData({ id: folderId, name: folderName });
  };

  const handleFolderExpand = async (folder, justToggle = false) => {
    const folderId = folder.id;
    const newExpanded = new Set(expandedFolders);

    if (justToggle) {
      if (newExpanded.has(folderId)) {
        newExpanded.delete(folderId);
      } else {
        newExpanded.add(folderId);
      }
      setExpandedFolders(newExpanded);
      return;
    }

    if (!newExpanded.has(folderId)) {
      newExpanded.add(folderId);
      setExpandedFolders(newExpanded);

      const newLoading = new Set(loadingFolders);
      newLoading.add(folderId);
      setLoadingFolders(newLoading);

      try {
        await dispatch(
          getFolderById({
            url: `${backend_api}documents/document/folder/`,
            token: token,
            folderId: folderId,
          })
        );
      } catch (error) {
        console.error("Error loading folder:", error);
        toast.error("Failed to load folder contents");
      } finally {
        const updatedLoading = new Set(loadingFolders);
        updatedLoading.delete(folderId);
        setLoadingFolders(updatedLoading);
      }
    } else {
      newExpanded.delete(folderId);
      setExpandedFolders(newExpanded);
    }
  };

  const onSubmit = async (data) => {
    if (!selectedFolderData || !selectedFolderData?.id) {
      toast.error("Please select a folder to upload files");
      return;
    }

    setUploading(true);
    const uploadedFileIds = [];

    try {
      const currentFolderResponse = await axios.get(
        `${backend_api}documents/document/folder/${selectedFolderData?.id}/`,
        {
          headers: {
            Authorization: `Token ${token}`,
          },
        }
      );

      const existingDocumentIds =
        currentFolderResponse.data.documents?.map((file) => file?.id) || [];

      for (const fileItem of data.files) {
        const formData = new FormData();
        formData.append("file", fileItem.file);
        formData.append("file_name", fileItem.file_name);

        const response = await axios.post(
          `${backend_api}documents/document/`,
          formData,
          {
            headers: {
              Authorization: `Token ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );

        if (response.data && response.data.id) {
          uploadedFileIds.push(response.data.id);
        }
      }

      if (uploadedFileIds.length > 0) {
        const allDocumentIds = [...existingDocumentIds, ...uploadedFileIds];

        const updateFolderResponse = await axios.patch(
          `${backend_api}documents/document/folder/${selectedFolderData?.id}/`,
          {
            documents: allDocumentIds,
          },
          {
            headers: {
              Authorization: `Token ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        toast.success(`Successfully uploaded ${uploadedFileIds.length} files`);
        closeDialog();
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload files. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box
          sx={{
            p: "1rem",
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            overflow: "hidden",
          }}
        >
          <DocDropZoneField
            uploadUrl="https://lexmom.com/upload"
            fileTypes={file_type}
            onUpload={handleMultiFileChange}
            multiple={true}
            allowFolder={false}
            selectedFileLength={fields.length}
          />

          {fields?.length > 0 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem",
                maxHeight: "200px",
                overflow: "auto",
                border: "1px solid #ddd",
                borderRadius: "0.5rem",
                p: "0.5rem",
                ...pageScrollStyle,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Files to Upload ({fields.length})
              </Typography>
              {fields?.map((field, i) => (
                <Box
                  key={i}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    p: "0.5rem",
                    border: "1px solid #4caf50",
                    borderRadius: "0.3rem",
                    bgcolor: "#f8f9fa",
                  }}
                >
                  <CheckCircleIcon
                    sx={{ color: "#4caf50", fontSize: "1rem" }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.8rem",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      flexGrow: 1,
                    }}
                  >
                    {field.file_name}
                  </Typography>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => remove(i)}
                    sx={{ p: 0.3 }}
                  >
                    <CloseRounded fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}

          <Box
            sx={{
              border: "1px solid #ddd",
              borderRadius: "0.5rem",
              p: "1rem",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, mb: "0.5rem" }}
            >
              Select Destination Folder
            </Typography>

            {selectedFolderData && selectedFolderData?.id && (
              <Box
                sx={{
                  p: "0.5rem",
                  bgcolor: "#e3f2fd",
                  borderRadius: "0.3rem",
                  mb: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                <FolderIcon sx={{ color: "#1976d2", fontSize: "1rem" }} />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Selected: {selectedFolderData?.name}
                </Typography>
              </Box>
            )}

            <Box
              sx={{
                maxHeight: "250px",
                overflow: "auto",
                border: "1px solid #eee",
                borderRadius: "0.3rem",
                p: "0.5rem",
              }}
            >
              {folderTree && folderTree.length > 0 ? (
                <FolderTree
                  folders={folderTree}
                  selectedFolder={selectedFolderData?.id}
                  onFolderSelect={handleFolderSelect}
                  onFolderExpand={handleFolderExpand}
                  expandedFolders={expandedFolders}
                  loadingFolders={loadingFolders}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No folders available
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            height: "fit-content",
            borderTop: "1px solid #ddd",
            position: "sticky",
            bottom: 0,
            backdropFilter: "blur(10px)",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            p: "0.5rem",
            zIndex: 1000,
            gap: "0.5rem",
          }}
        >
          <Button size="small" variant="outlined" onClick={() => closeDialog()}>
            Cancel
          </Button>
          <LoadingButton
            size="small"
            variant="contained"
            color="primary"
            type="submit"
            loading={uploading}
            disabled={fields.length === 0 || !selectedFolderData?.id}
          >
            Upload {fields.length} File{fields.length !== 1 ? "s" : ""}
          </LoadingButton>
        </Box>
      </form>
    </Box>
  );
};

export default UploadFileForm;
